import React, { useRef, useState, useEffect, useCallback } from "react";
import "./HomePage.css";
import headerImage from "../images/HomePage_header.png";
import rollingPin from "../images/HomePage_rollingPin.png";
import platter from "../images/HomePage_platter.png";
import chef from "../images/HomePage_chef.png";
import globe from "../images/HomePage_globe.png";
import globe2 from "../images/HomePage_globe2.png";
import forkImg from "../images/HomePage_fork.png";
import knifeImg from "../images/HomePage_knife.png";
import cycleImg from "../images/HomePage_cycle.png";
import handImg from "../images/HomePage_hand.png";
import EventCard from "../components/EventCard";
import "../components/EventCard.css";
import PastEventCard from "../components/PastEventCard";
import leftArrow from '../images/HomePage_leftArrow.svg';
import rightArrow from '../images/HomePage_rightArrow.svg';
import burgerImg from "../images/HomePage_burger.png";
import burgerImg2 from "../images/HomePage_burger2.png";
import burgerImg3 from "../images/HomePage_burger3.png";

const API_URL = process.env.REACT_APP_API_URL || "";

const eventPhotoContext = require.context(
    "../images/events",
    false,
    /event_photo_\d+\.(png|jpe?g)$/i
);

const eventPhotos = eventPhotoContext.keys()
    .sort((first, second) => {
        const firstNumber = Number(first.match(/\d+/)?.[0] || 0);
        const secondNumber = Number(second.match(/\d+/)?.[0] || 0);
        return firstNumber - secondNumber;
    })
    .map((path, index) => ({
        src: eventPhotoContext(path),
        alt: `CKC past event highlight ${index + 1}`,
    }));

const getCardImage = (imageName) => {
    if (!imageName) return platter;

    try {
        return require(`../images/${imageName}`);
    } catch {
        return platter;
    }
};

const getPrefersReducedMotion = () => Boolean(
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const getLoopWidth = (el, originalItemCount) => {
    const firstItem = el.children[0];
    const duplicateStartItem = el.children[originalItemCount];

    if (!firstItem || !duplicateStartItem) {
        return el.scrollWidth / 2;
    }

    return duplicateStartItem.offsetLeft - firstItem.offsetLeft;
};

const AnimatedStatNumber = ({ target, suffix = "", duration = 1000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        const prefersReducedMotion = getPrefersReducedMotion();

        if (prefersReducedMotion) {
            setCount(target);
            return undefined;
        }

        setCount(0);

        let animationFrame;
        let startTime;

        const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);

        const updateCount = (timestamp) => {
            if (!startTime) startTime = timestamp;

            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const nextCount = Math.round(target * easeOutCubic(progress));

            setCount(nextCount);

            if (progress < 1) {
                animationFrame = window.requestAnimationFrame(updateCount);
            } else {
                setCount(target);
            }
        };

        animationFrame = window.requestAnimationFrame(updateCount);

        return () => {
            window.cancelAnimationFrame(animationFrame);
        };
    }, [target, duration]);

    return <div className="stat-number">{count}{suffix}</div>;
};

const HomePage = () => {
    const listRef = useRef(null);
    const pastListRef = useRef(null);
    const [isUpcomingPaused] = useState(() => getPrefersReducedMotion());
    const [isPastPaused] = useState(() => getPrefersReducedMotion());
    const [isReviewsPaused] = useState(() => getPrefersReducedMotion());
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [latestBlogsLoading, setLatestBlogsLoading] = useState(true);
    const [latestBlogsError, setLatestBlogsError] = useState("");
    const [pastEvents, setPastEvents] = useState([]);
    const [pastEventsLoading, setPastEventsLoading] = useState(true);
    const [pastEventsError, setPastEventsError] = useState("");
    const [eventPhotoIndex, setEventPhotoIndex] = useState(0);
    const [isEventGalleryPaused, setIsEventGalleryPaused] = useState(() => getPrefersReducedMotion());

    const showEventPhoto = useCallback((direction) => {
        setEventPhotoIndex((currentIndex) => (
            (currentIndex + direction + eventPhotos.length) % eventPhotos.length
        ));
    }, []);

    useEffect(() => {
        if (isEventGalleryPaused || eventPhotos.length <= 1) return undefined;

        const intervalId = window.setInterval(() => {
            setEventPhotoIndex((currentIndex) => (currentIndex + 1) % eventPhotos.length);
        }, 4000);

        return () => window.clearInterval(intervalId);
    }, [isEventGalleryPaused]);

    useEffect(() => {
        const controller = new AbortController();

        fetch(`${API_URL}/api/blogs?limit=4`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Unable to load the latest blogs.");
                }
                return response.json();
            })
            .then((blogs) => {
                const recentBlogs = Array.isArray(blogs) ? blogs.slice(0, 4) : [];
                setLatestBlogs(recentBlogs);
                setLatestBlogsError("");
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setLatestBlogs([]);
                    setLatestBlogsError("We couldn't load the latest blogs. Please try again later.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLatestBlogsLoading(false);
                }
            });

        return () => controller.abort();
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        fetch(`${API_URL}/api/workshops/past?limit=4`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Unable to load past events.");
                }
                return response.json();
            })
            .then((workshops) => {
                const recentPastEvents = Array.isArray(workshops) ? workshops.slice(0, 4) : [];
                setPastEvents(recentPastEvents);
                setPastEventsError("");
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setPastEvents([]);
                    setPastEventsError("We couldn't load past events. Please try again later.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setPastEventsLoading(false);
                }
            });

        return () => controller.abort();
    }, []);

    const recenterLoopScroll = useCallback((el, originalItemCount) => {
        const loopWidth = getLoopWidth(el, originalItemCount);

        if (!loopWidth) return;

        if (el.scrollLeft < loopWidth * 0.5) {
            el.scrollLeft += loopWidth;
        } else if (el.scrollLeft > loopWidth * 1.5) {
            el.scrollLeft -= loopWidth;
        }
    }, []);

    const normalizePastLoopScroll = useCallback((el, originalItemCount, buffer = 0) => {
        const loopWidth = getLoopWidth(el, originalItemCount);

        if (!loopWidth) return;

        if (el.scrollLeft >= loopWidth + buffer) {
            el.scrollLeft -= loopWidth;
        }
    }, []);

    const setLoopStart = useCallback((el, originalItemCount) => {
        const firstCardInMiddleSet = el.children[originalItemCount];

        if (firstCardInMiddleSet) {
            el.scrollLeft = firstCardInMiddleSet.offsetLeft
                - ((el.clientWidth - firstCardInMiddleSet.offsetWidth) / 2);
        }
    }, []);

    const scrollLoopToCard = useCallback((ref, dir = 1) => {
        const el = ref.current;
        if (!el) return;

        const cards = Array.from(el.children);
        if (!cards.length) return;

        const viewportCenter = el.scrollLeft + (el.clientWidth / 2);
        const currentIndex = cards.reduce((closestIndex, card, cardIndex) => {
            const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
            const closestCard = cards[closestIndex];
            const closestCenter = closestCard.offsetLeft + (closestCard.offsetWidth / 2);

            return Math.abs(cardCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter)
                ? cardIndex
                : closestIndex;
        }, 0);
        const targetIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + dir));
        const targetCard = cards[targetIndex];
        const targetLeft = targetCard.offsetLeft - ((el.clientWidth - targetCard.offsetWidth) / 2);

        el.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }, []);

    // Scrolling for event (upcoming) carousel
    const scrollByAmount = useCallback((dir = 1) => {
        scrollLoopToCard(listRef, dir);
    }, [scrollLoopToCard]);

    // Scrolling for event (past) carousel
    const scrollPastByAmount = useCallback((dir = 1) => {
        const el = pastListRef.current;
        if (!el) return;

        const loopWidth = getLoopWidth(el, pastEvents.length);
        if (dir < 0 && el.scrollLeft <= 8 && loopWidth) {
            el.scrollLeft += loopWidth;
        } else {
            normalizePastLoopScroll(el, pastEvents.length, 8);
        }

        scrollLoopToCard(pastListRef, dir);
    }, [normalizePastLoopScroll, pastEvents.length, scrollLoopToCard]);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return undefined;

        const initialize = () => {
            setLoopStart(el, latestBlogs.length);
        };
        const recenter = () => {
            recenterLoopScroll(el, latestBlogs.length);
        };

        const animationFrame = window.requestAnimationFrame(initialize);
        el.addEventListener('scroll', recenter, { passive: true });
        window.addEventListener('resize', initialize);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            el.removeEventListener('scroll', recenter);
            window.removeEventListener('resize', initialize);
        };
    }, [latestBlogs.length, recenterLoopScroll, setLoopStart]);

    // Fade visibility for upcoming events scrolling
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const updateFades = () => {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            setShowLeftFade(scrollLeft > 8);
            setShowRightFade(scrollLeft + clientWidth < scrollWidth - 8);
        };

        updateFades();

        el.addEventListener('scroll', updateFades, { passive: true });
        window.addEventListener('resize', updateFades);

        return () => {
            el.removeEventListener('scroll', updateFades);
            window.removeEventListener('resize', updateFades);
        };
    }, [latestBlogs.length]);

    const reviews = [
        {
            id: 1,
            image: burgerImg,
            quote: 'Wonderful And Informative Workshop - Learned A Lot! Thank You To The Organizers, Hosts, And Presenters All.',
            author: 'Samantha Seiter'
            , role: ''
        },
        {
            id: 2,
            image: burgerImg2,
            quote: "When I first joined CKC, I had no idea that it would have the amazing impact that it did. I've developed amazing leadership and social skills, all of which have helped me land internships and jobs.",
            author: 'Taylor L.',
            role: 'Digital Marketing & Engagement Manager'
        },
        {
            id: 3,
            image: burgerImg3,
            quote: 'CKC gave me the chance to learn and grow so much over the course of this semester. I was able to reach out and connect with so many driven and independent women who inspire me to work for and reach my entrepreneurial goals.',
            author: 'Michelle H.',
            role: 'Fundraising & Sponsorship Intern'
        }
    ];

    const [index, setIndex] = useState(0);
    const [prevIndex, setPrevIndex] = useState(null);
    const [animating, setAnimating] = useState(false);
    const [direction, setDirection] = useState('right');

    const len = reviews.length;

    // Sliding effect for review section
    const go = useCallback((dir) => {
        if (animating) return;
        const next = (index + dir + len) % len;
        setPrevIndex(index);
        setIndex(next);
        setDirection(dir === 1 ? 'right' : 'left');
        setAnimating(true);
        window.setTimeout(() => {
            setAnimating(false);
            setPrevIndex(null);
        }, 360);
    }, [animating, index, len]);

    const prev = useCallback(() => go(-1), [go]);
    const next = useCallback(() => go(1), [go]);

    useEffect(() => {
        if (isUpcomingPaused) return undefined;

        const interval = window.setInterval(() => {
            scrollByAmount(1);
        }, 5000);

        return () => {
            window.clearInterval(interval);
        };
    }, [isUpcomingPaused, scrollByAmount]);

    useEffect(() => {
        if (isPastPaused) return undefined;

        const interval = window.setInterval(() => {
            scrollPastByAmount(1);
        }, 5000);

        return () => {
            window.clearInterval(interval);
        };
    }, [isPastPaused, scrollPastByAmount]);

    useEffect(() => {
        if (isReviewsPaused || animating) return undefined;

        const interval = window.setInterval(() => {
            next();
        }, 5000);

        return () => {
            window.clearInterval(interval);
        };
    }, [animating, isReviewsPaused, next]);

    return (
        <div className="home-page">
            <header className="home-header">
                <img
                    src={headerImage}
                    alt="Home Header"
                    className="header-image-home"
                />
                <div className="header-text-home">
                    <h1 className="home-title">Cultured Kids Cuisine</h1>
                    <div className="header-subtitle">Fostering cultural awareness through food</div>
                </div>
            </header>

            <section className="mission-section">
                <div className="mission-content">
                    <h2 className="mission-title">Our Mission</h2>
                    <h3 className="mission-subtitle">Why We Do What We Do</h3>
                    <p className="mission-text">
                        Food, being integral to all cultures globally, is the ideal educational
                        vessel for promoting cultural learning. There has been a lack of
                        implementation of cultural education through food. Research has
                        indicated that early exposure to different cultures significantly
                        reduces prejudice, stereotyping, and discrimination in adulthood.
                        Each encounter with diverse perspectives fosters interpersonal
                        skills, creativity, and self-expression.
                    </p>

                    <div className="rolling-pin-deco">
                        <img src={rollingPin} alt="rolling pin" />
                    </div>

                    <div className="stats-row">
                        <div className="stat">
                            <img src={platter} alt="platter" className="stat-icon" />
                            <AnimatedStatNumber target={176} />
                            <div className="stat-label">Educational Culinary Shops</div>
                        </div>

                        <div className="stat">
                            <img src={chef} alt="chef" className="stat-icon" />
                            <AnimatedStatNumber target={768} />
                            <div className="stat-label">Little Chefs Educated</div>
                        </div>

                        <div className="stat">
                            <img src={globe} alt="globe" className="stat-icon" />
                            <AnimatedStatNumber target={100} suffix="+" />
                            <div className="stat-label">Participants from other countries</div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="plate-section">
                <div className="plate-wrapper">
                    <img src={knifeImg} alt="knife" className="side-knife" />
                    <img src={forkImg} alt="fork" className="side-fork" />

                    <div className="plate-circle">
                        <div className="plate-inner">
                            {/* <h3 className="plate-title">Why We Do<br />What We Do</h3> */}

                            <div className="plate-items">
                                <div className="plate-item left" style={{ '--deg': '180deg' }}>
                                    <img src={cycleImg} alt="cycle" />
                                    <p>Exposing youth to more nutritious and meaningful food options</p>
                                </div>

                                <div className="plate-item bottom" style={{ '--deg': '90deg' }}>
                                    <img src={handImg} alt="hand" />
                                    <p>Offering the resources necessary to spearhead a world of love and cultural competence</p>
                                </div>

                                <div className="plate-item right" style={{ '--deg': '0deg' }}>
                                    <img src={globe2} alt="globe" />
                                    <p>Spreading cultural appreciation and world peace by sharing knowledge through food</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                className="event-gallery-section"
                aria-labelledby="event-gallery-title"
                onFocus={() => setIsEventGalleryPaused(true)}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setIsEventGalleryPaused(getPrefersReducedMotion());
                    }
                }}
            >
                <div className="event-gallery-heading">
                    <p>Scenes from our community</p>
                    <h2 id="event-gallery-title">Past Event Highlights</h2>
                </div>

                <div className="event-gallery-carousel">
                    <button
                        type="button"
                        className="event-gallery-arrow previous"
                        onClick={() => showEventPhoto(-1)}
                        aria-label="Show previous event photo"
                    >
                        <img src={leftArrow} alt="" />
                    </button>

                    <div className="event-gallery-viewport" aria-live="polite">
                        <div
                            className="event-gallery-track"
                            style={{ transform: `translateX(-${eventPhotoIndex * 100}%)` }}
                        >
                            {eventPhotos.map((photo, photoIndex) => (
                                <figure
                                    className="event-gallery-slide"
                                    key={photo.src}
                                    aria-hidden={photoIndex !== eventPhotoIndex}
                                >
                                    <img src={photo.src} alt={photo.alt} />
                                </figure>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="event-gallery-arrow next"
                        onClick={() => showEventPhoto(1)}
                        aria-label="Show next event photo"
                    >
                        <img src={rightArrow} alt="" />
                    </button>
                </div>

                <div className="event-gallery-pagination" aria-label="Choose an event photo">
                    {eventPhotos.map((photo, photoIndex) => (
                        <button
                            type="button"
                            key={photo.src}
                            className={photoIndex === eventPhotoIndex ? "active" : ""}
                            onClick={() => setEventPhotoIndex(photoIndex)}
                            aria-label={`Show event photo ${photoIndex + 1}`}
                            aria-current={photoIndex === eventPhotoIndex ? "true" : undefined}
                        />
                    ))}
                </div>
            </section>

            <section className="events-section">
                <h2 className="events-title">Latest Blogs</h2>

                {latestBlogsLoading && <p className="events-status">Loading latest blogs...</p>}
                {!latestBlogsLoading && latestBlogsError && (
                    <p className="events-status events-status-error" role="alert">{latestBlogsError}</p>
                )}
                {!latestBlogsLoading && !latestBlogsError && latestBlogs.length === 0 && (
                    <p className="events-status">No blog posts are available yet.</p>
                )}
                {!latestBlogsLoading && !latestBlogsError && latestBlogs.length > 0 && (
                    <div className="events-carousel">
                        <button onClick={() => scrollByAmount(-1)} className="events-arrow left" aria-label="Scroll left">
                            <img src={leftArrow} alt="left" />
                        </button>

                        {showLeftFade && <div className="events-fade left" aria-hidden="true"></div>}

                        <div className="events-list" ref={listRef}>
                            {[...latestBlogs, ...latestBlogs, ...latestBlogs].map((blog, cardIndex) => (
                                <EventCard
                                    key={`latest-blog-${cardIndex}-${blog.slug || blog._id}`}
                                    image={getCardImage(blog.image)}
                                    date={blog.date}
                                    title={blog.title}
                                    description={blog.excerpt}
                                    actionUrl={`/blogs/${blog.slug}`}
                                    actionLabel="Read More"
                                />
                            ))}
                        </div>

                        {showRightFade && <div className="events-fade right" aria-hidden="true"></div>}

                        <button onClick={() => scrollByAmount(1)} className="events-arrow right" aria-label="Scroll right">
                            <img src={rightArrow} alt="right" />
                        </button>
                    </div>
                )}
            </section>

            <section className="past-events-section">
                <h2 className="events-title">Past Events</h2>

                {pastEventsLoading && <p className="events-status">Loading past events...</p>}
                {!pastEventsLoading && pastEventsError && (
                    <p className="events-status events-status-error" role="alert">{pastEventsError}</p>
                )}
                {!pastEventsLoading && !pastEventsError && pastEvents.length === 0 && (
                    <p className="events-status">No past events are available yet.</p>
                )}
                {!pastEventsLoading && !pastEventsError && pastEvents.length > 0 && (
                    <div className="events-carousel">
                        <button onClick={() => scrollPastByAmount(-1)} className="events-arrow left" aria-label="Scroll left">
                            <img src={leftArrow} alt="left" />
                        </button>

                        <div className="events-list" ref={pastListRef}>
                            {[...pastEvents, ...pastEvents].map((ev, cardIndex) => (
                                <PastEventCard
                                    key={`past-${cardIndex}-${ev._id || ev.id}`}
                                    image={getCardImage(ev.image)}
                                    date={ev.date}
                                    title={ev.name || ev.title}
                                    subtitle={ev.subtitle || ev.time}
                                    description={ev.description || ""}
                                    readMoreUrl={ev.readMoreUrl || ev.link}
                                />
                            ))}
                        </div>

                        <button onClick={() => scrollPastByAmount(1)} className="events-arrow right" aria-label="Scroll right">
                            <img src={rightArrow} alt="right" />
                        </button>

                        <div className="events-fade" aria-hidden="true"></div>
                    </div>
                )}
            </section>

            <section className="reviews-section">
                    <div className={`reviews-carousel ${direction === 'right' ? 'dir-right' : 'dir-left'}`}>
                        <button className="reviews-arrow left" onClick={prev} aria-label="Previous review">
                            <img src={leftArrow} alt="left" />
                        </button>

                        <div className="reviews-stage">
                            {reviews.map((r, i) => {
                                let cls = 'review-item';
                                if (animating) {
                                    if (i === index) cls += ` enter-${direction}`;
                                    if (i === prevIndex) cls += ` exit-${direction === 'right' ? 'left' : 'right'}`;
                                } else {
                                    if (i === index) cls += ' active';
                                }

                                if (i % 2 === 1) cls += ' reverse';

                                return (
                                    <div key={r.id} className={cls} aria-hidden={i !== index && i !== prevIndex}>
                                        <img className="review-image" src={r.image} alt={`review ${r.author}`} />
                                        <div>
                                            <blockquote className="review-quote">{r.quote}</blockquote>
                                            <div className="review-author">{r.author}</div>
                                            {r.role ? <div className="review-role">{r.role}</div> : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button className="reviews-arrow right" onClick={next} aria-label="Next review">
                            <img src={rightArrow} alt="right" />
                        </button>
                    </div>

                    <div className="reviews-dots" role="tablist">
                        {reviews.map((r, i) => (
                            <button
                                key={r.id}
                                className={`reviews-dot ${i === index ? 'active' : ''}`}
                                onClick={() => {
                                    if (i === index || animating) return;
                                    const dir = i > index ? 1 : -1;
                                    go(dir);
                                }}
                                aria-label={`Show review ${i + 1}`}
                            />
                        ))}
                    </div>
                </section>
        </div>
    );
};

export default HomePage;
